import { useDebouncedValue } from '@src/shared/hooks';
import Avatar from '@src/shared/ui/Avatar';
import { IconButton } from '@src/shared/ui/Button';
import Chip from '@src/shared/ui/Chip';
import { DetailsIcon } from '@src/shared/ui/icons';
import { Box, Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useClientContactMatches } from '../model';
import type { ContactMatch } from '../types';

const DEBOUNCE_MS = 300;
const NO_IDS: ReadonlySet<number> = new Set();

const StyleSheet = {
  panel: {
    backgroundColor: 'grey.100',
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
    flex: '0 0 380px',
    alignSelf: 'stretch',
    overflow: 'auto',
    p: 2.5,
  },
  // Below other panels: it takes the height they leave.
  columnPanel: { flex: '1 1 auto', minHeight: 0 },
  card: {
    alignItems: 'flex-start',
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius: 1,
    cursor: 'pointer',
    display: 'flex',
    gap: 0.75,
    px: 1.25,
    py: 1.25,
    textAlign: 'left',
    width: '100%',
    '&:hover': { borderColor: 'grey.600' },
  },
  // Light red with a red border (design).
  selectedCard: {
    backgroundColor: 'red.100',
    borderColor: 'primary.main',
    '&:hover': { borderColor: 'primary.main' },
  },
  addedCard: {
    cursor: 'default',
    '&:hover': { borderColor: 'grey.200' },
  },
  avatar: {
    borderRadius: 1,
    fontSize: 14,
    height: 32,
    width: 32,
  },
  details: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  // Two lines as high as the avatar beside them (design); on the text
  // itself, over the line height of its variant.
  line: { lineHeight: '16px' },
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

type MatchingContactsPanelProps = {
  familyName: string;
  firstName: string;
  selected: ContactMatch | null;
  /** The picked contact, or null when the picked one is clicked again. */
  onSelectedChange: (contact: ContactMatch | null) => void;
  /** Contacts the wizard has taken already: shown, but not to be picked. */
  addedIds?: ReadonlySet<number>;
  /** It stands in a column of panels, not beside the step. */
  inColumn?: boolean;
  /** The client and job of a contact below its name. */
  showPosition?: boolean;
};

/**
 * "Potentially Matching Contacts": existing contacts with the typed name,
 * for a wizard that adds a contact. The wizard keeps the names and the
 * picked contact in its form and decides what picking one fills in.
 */
function MatchingContactsPanel({
  familyName,
  firstName,
  selected,
  onSelectedChange,
  addedIds = NO_IDS,
  inColumn = false,
  showPosition = true,
}: MatchingContactsPanelProps) {
  const { t } = useTranslation();
  const { data: results = [] } = useClientContactMatches({
    familyName: useDebouncedValue(familyName, DEBOUNCE_MS),
    firstName: useDebouncedValue(firstName, DEBOUNCE_MS),
  });
  // Picking usually fills in the contact's own names, which searches again;
  // keep its card first so it can be deselected even when it drops out.
  const matches = selected
    ? [selected, ...results.filter((contact) => contact.id !== selected.id)]
    : results;

  if (matches.length === 0) {
    return null;
  }

  const select = (contact: ContactMatch) => {
    onSelectedChange(selected?.id === contact.id ? null : contact);
  };

  return (
    <Box sx={[StyleSheet.panel, inColumn && StyleSheet.columnPanel]}>
      <Typography variant="h5" component="h3">
        {t(
          'clients.add_contact.matching_contacts_title',
          'Potentially Matching Contacts - Please Select',
        )}
      </Typography>
      <Stack sx={{ gap: 1.25 }}>
        {matches.map((contact) => {
          const isSelected = selected?.id === contact.id;
          const isAdded = !isSelected && addedIds.has(contact.id);
          const position = [contact.clients[0]?.name, contact.job]
            .filter(Boolean)
            .join(', ');
          return (
            <Box
              key={contact.id}
              {...(isAdded
                ? { 'aria-disabled': true }
                : {
                    role: 'button',
                    tabIndex: 0,
                    'aria-pressed': isSelected,
                    onClick: () => select(contact),
                    onKeyDown: (event: KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        select(contact);
                      }
                    },
                  })}
              sx={[
                StyleSheet.card,
                isSelected && StyleSheet.selectedCard,
                isAdded && StyleSheet.addedCard,
              ]}
            >
              <Avatar
                variant="rounded"
                src={contact.portrait ?? undefined}
                alt={contact.name}
                sx={StyleSheet.avatar}
              >
                {initials(contact.name)}
              </Avatar>
              <Box sx={StyleSheet.details}>
                <Typography
                  variant="h5"
                  component="span"
                  noWrap
                  sx={StyleSheet.line}
                >
                  {contact.name}
                </Typography>
                {showPosition && position ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    noWrap
                    sx={StyleSheet.line}
                  >
                    {position}
                  </Typography>
                ) : null}
                {contact.email ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    noWrap
                    sx={StyleSheet.line}
                  >
                    {contact.email}
                  </Typography>
                ) : null}
              </Box>
              {isAdded ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('clients.add_client.contact_added', 'Added')}
                  sx={{ alignSelf: 'center' }}
                />
              ) : null}
              {/* Hidden for a contact of a segment the user may not view. */}
              {contact.canView ? (
                <IconButton
                  size="small"
                  tooltipTitle={t(
                    'clients.add_contact.open_contact',
                    'Open contact',
                  )}
                  onClick={(event) => {
                    // Opens beside the wizard, which keeps what was entered.
                    event.stopPropagation();
                    window.open(
                      `/clients/representatives/${contact.id}`,
                      '_blank',
                      'noopener',
                    );
                  }}
                  sx={{ alignSelf: 'center' }}
                >
                  <DetailsIcon />
                </IconButton>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

MatchingContactsPanel.displayName = 'MatchingContactsPanel';
export default MatchingContactsPanel;
